import React from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

const transactions = [
  {
    id: 1, type: 'buy', coin: 'BTC', coinName: 'Bitcoin',
    amount: 0.025, value: 1680.86, status: 'completed', time: '2 min ago',
  },
  {
    id: 2, type: 'sell', coin: 'ETH', coinName: 'Ethereum',
    amount: 1.5, value: 4868.67, status: 'completed', time: '15 min ago',
  },
  {
    id: 3, type: 'swap', coin: 'SOL', toCoin: 'USDT', coinName: 'Solana',
    amount: 10, value: 1425.60, status: 'pending', time: '1 hour ago',
  },
  {
    id: 4, type: 'buy', coin: 'ADA', coinName: 'Cardano',
    amount: 500, value: 228.35, status: 'completed', time: '3 hours ago',
  },
  {
    id: 5, type: 'sell', coin: 'BNB', coinName: 'BNB',
    amount: 2, value: 1196.46, status: 'failed', time: '5 hours ago',
  },
  {
    id: 6, type: 'buy', coin: 'DOGE', coinName: 'Dogecoin',
    amount: 1000, value: 123.40, status: 'completed', time: '6 hours ago',
  },
  {
    id: 7, type: 'swap', coin: 'ETH', toCoin: 'BTC', coinName: 'Ethereum',
    amount: 2.5, value: 8122.45, status: 'completed', time: '1 day ago',
  },
];

const getTypeIcon = (type) => {
  switch (type) {
    case 'buy': return 'fa-arrow-down';
    case 'sell': return 'fa-arrow-up';
    case 'swap': return 'fa-arrow-right-arrow-left';
    default: return 'fa-circle';
  }
};

const getTypeColor = (type) => {
  switch (type) {
    case 'buy': return 'bg-success/10 text-success';
    case 'sell': return 'bg-destructive/10 text-destructive';
    case 'swap': return 'bg-primary/10 text-primary';
    default: return 'bg-muted text-muted-foreground';
  }
};

const getStatusBadge = (status) => {
  switch (status) {
    case 'completed':
      return <Badge variant="success" className="text-xs"><i className="fa-solid fa-check mr-1"></i>Completed</Badge>;
    case 'pending':
      return <Badge variant="warning" className="text-xs"><i className="fa-solid fa-clock mr-1"></i>Pending</Badge>;
    case 'failed':
      return <Badge variant="destructive" className="text-xs"><i className="fa-solid fa-xmark mr-1"></i>Failed</Badge>;
    default:
      return null;
  }
};

export const TransactionHistory = ({ className, maxItems, showHeader = true }) => {
  const displayTransactions = maxItems ? transactions.slice(0, maxItems) : transactions;

  return (
    <Card className={cn("", className)}>
      {showHeader && (
        <CardHeader className="pb-3 px-4 sm:px-6">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Recent Transactions</CardTitle>
            <Button variant="ghost" size="sm" className="text-primary text-xs h-7">
              View All
              <i className="fa-solid fa-arrow-right ml-1 text-[10px]"></i>
            </Button>
          </div>
        </CardHeader>
      )}
      <CardContent className={cn("px-4 sm:px-6", !showHeader && "pt-0")}>
        <ScrollArea className={maxItems ? "" : "h-[400px]"}>
          <div className="space-y-2">
            {displayTransactions.map((tx) => (
              <div
                key={tx.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
              >
                {/* Type Icon */}
                <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", getTypeColor(tx.type))}>
                  <i className={cn("fa-solid text-sm", getTypeIcon(tx.type))}></i>
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm capitalize">{tx.type}</span>
                    <span className="text-sm text-muted-foreground">{tx.coin}</span>
                    {tx.type === 'swap' && (
                      <>
                        <i className="fa-solid fa-arrow-right text-[10px] text-muted-foreground"></i>
                        <span className="text-sm text-muted-foreground">{tx.toCoin}</span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{tx.time}</p>
                </div>

                {/* Amount & Status */}
                <div className="text-right shrink-0">
                  <p className={cn(
                    "font-mono text-sm font-medium",
                    tx.type === 'buy' ? 'text-success' : tx.type === 'sell' ? 'text-destructive' : ''
                  )}>
                    {tx.type === 'buy' ? '+' : tx.type === 'sell' ? '-' : ''}{tx.amount} {tx.coin}
                  </p>
                  <p className="text-xs text-muted-foreground font-mono mt-0.5">
                    ${tx.value.toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default TransactionHistory;
